import { Node, mergeAttributes } from '@tiptap/core';
import { NodeViewWrapper, NodeViewProps, ReactNodeViewRenderer } from '@tiptap/react';
import React from 'react';

// React component to render iframe in the editor
const IframeNodeView: React.FC<NodeViewProps> = ({ node, selected }) => {
  const { src, width, height, frameborder, style } = node.attrs;

  return (
    <NodeViewWrapper className="iframe-wrapper my-4">
      <div 
        className={`relative rounded-lg overflow-hidden border ${selected ? 'ring-2 ring-primary' : ''}`}
        style={{ maxWidth: '100%' }}
      >
        <iframe
          src={src}
          width={width || '100%'}
          height={height || '400'}
          frameBorder={frameborder || '0'}
          style={{ 
            borderRadius: '8px', 
            display: 'block',
            ...parseStyleString(style)
          }}
          allowFullScreen
          loading="lazy"
        />
      </div>
    </NodeViewWrapper>
  );
};

// Parse inline style string to object
function parseStyleString(styleString: string | null | undefined): React.CSSProperties {
  if (!styleString) return {};
  
  const styles: Record<string, string> = {};
  styleString.split(';').forEach(rule => {
    const [property, value] = rule.split(':').map(s => s?.trim());
    if (property && value) {
      // Convert kebab-case to camelCase
      const camelProperty = property.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());
      styles[camelProperty] = value;
    }
  });
  return styles as React.CSSProperties;
}

// Custom Iframe extension for TipTap
export const IframeExtension = Node.create({
  name: 'iframe',
  
  group: 'block',
  
  atom: true,
  
  addAttributes() {
    return {
      src: {
        default: null,
      },
      width: {
        default: '100%',
      },
      height: {
        default: '400',
      },
      frameborder: {
        default: '0',
      },
      style: {
        default: null,
      },
      allowfullscreen: {
        default: true,
      },
    };
  },
  
  parseHTML() {
    return [
      {
        tag: 'iframe[src]',
      },
    ];
  },
  
  renderHTML({ HTMLAttributes }) {
    return ['iframe', mergeAttributes(HTMLAttributes)];
  },
  
  addNodeView() {
    return ReactNodeViewRenderer(IframeNodeView);
  },
});

export default IframeExtension;
